/**
 * A host-node registry, and the one thing standing between D331's operator notice and R9 §5's
 * fail-open rule.
 *
 * `screens/home/HomeScreen.tsx` is one of the three roots `infra/tests/client-config-client.test.ts`
 * walks (`ble/`, `screens/home/`, `screens/gate/`) — it may not import `web/src/config/`, not even
 * transitively through `components/`'s barrel, because that walk follows every relative import and
 * fails the moment the closure enters the config directory. So Home cannot read the document,
 * resolve a locale, or know whether a notice was dismissed — it can only offer the one thing that
 * costs it nothing: the DOM node the notice may render into, in the exact spot D275's banner
 * already occupies.
 *
 * This module carries zero imports on purpose — a file `screens/home/` is allowed to reach must
 * itself reach nothing R9 §5 forbids, so the guarantee has to hold structurally, not by review.
 * `homeNoticeHost.test.ts` pins that with a source-text assertion the same way
 * `client-config-client.test.ts` pins the rest of D328.
 *
 * `config/HomeNoticePortal.tsx` — mounted once, outside the closure, from `main.tsx` — is the only
 * reader: it holds the document, resolves the text and the dismissal, and portals the result into
 * whatever this module currently points at. Nothing here imports it or knows it exists, so the
 * dependency is strictly one-directional: Home registers, the portal source reads.
 */

let host: HTMLDivElement | null = null
const listeners = new Set<() => void>()

/**
 * Called from a callback `ref` on Home's own placeholder `<div>` — React invokes a callback ref
 * with the node on mount/update and with `null` on unmount, so this is the whole of Home's
 * lifecycle participation. `updateInstallReady` renders `UpdateReadyBanner` in this div's place
 * instead of the div itself, so a `null` here also covers "D275's banner has the slot right now."
 */
export function registerHomeNoticeHost(next: HTMLDivElement | null): void {
  if (next === host) return
  host = next
  for (const listener of listeners) listener()
}

/** `useSyncExternalStore`'s subscribe half, for `HomeNoticePortal`. */
export function subscribeHomeNoticeHost(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** `useSyncExternalStore`'s snapshot half. `null` means *no slot right now* — render nothing. */
export function getHomeNoticeHost(): HTMLDivElement | null {
  return host
}

/** Test seam. */
export function __resetHomeNoticeHostForTests(): void {
  host = null
  listeners.clear()
}
